import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Select, Tabs, Tooltip } from 'antd';
import { ExclamationCircleOutlined, InfoCircleOutlined, PrinterOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import { useFeedback } from '../utils/feedback';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import FixedCosts from './FixedCosts';
import CostInvoices from '../modules/companyCosts/CostInvoices';
import IncomeStatementWorkspace from '../modules/reports/incomeStatement/IncomeStatementWorkspace';
import OrdersInResultSection from '../modules/reports/incomeStatement/OrdersInResultSection';
import SalesRepsSection from '../modules/reports/salesReps/SalesRepsSection';
import LiquiditySection from '../modules/reports/liquidity/LiquiditySection';
import type { PivotOrderInfo } from '../modules/reports/incomeStatement/orderPivot';
import ManagementDashboard from '../modules/reports/managementDashboard/ManagementDashboard';
import { useDashboardData } from '../modules/reports/managementDashboard/useDashboardData';
import { DASHBOARD_SCENARIOS, type DashboardScenario, type DashboardView } from '../modules/reports/managementDashboard/dashboardModel';
import {
  monthLongName,
  periodFromRangeValue,
  quarterRoman,
  readReportPeriod,
  reportPeriodMonths,
  reportPeriodRange,
  reportRangeValue,
  writeReportPeriod,
  type ReportPeriod,
} from '../modules/reports/reportPeriod';
import {
  displayMarginPercent,
  MARGIN_BUCKET_THRESHOLDS,
  productAnalysisIdentity,
} from '../modules/reports/profitabilityAnalysis/analysis';
import type { AnalysisProduct } from '../modules/reports/profitabilityAnalysis/analysis';
import { ReportSection } from '../modules/reports/ReportTab';
import '../modules/reports/ReportTab.css';
import './Profitability.css';

// ─── DTOs ────────────────────────────────────────────────────────────────────

interface OrderRow {
  id: string;
  orderNumber: string;
  manualOrderNumber?: string;
  useManualNumbering?: boolean;
  customer?: { id?: string; name: string };
  accountManager?: { name?: string } | null;
  status: string;
  revenue: number;
  // `null` means an unknown cost/margin (e.g. a pending WAREHOUSE_FIFO batch),
  // never a real zero — see analysis.ts `AnalysisOrder`.
  totalCost: number | null;
  warehouseCostPending?: boolean;
  margin: number | null;
  marginPercent: number | null;
  products?: AnalysisProduct[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function num(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

// Cost/margin/marginPercent stay `null` (an unknown pending-cost order),
// never coerced to a fake 0 — see analysis.ts `AnalysisOrder`.
function nullableNum(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function displayOrderNumber(o: Pick<OrderRow, 'orderNumber' | 'manualOrderNumber' | 'useManualNumbering'>): string {
  return o.useManualNumbering && o.manualOrderNumber ? o.manualOrderNumber : o.orderNumber;
}

// ─── Component ───────────────────────────────────────────────────────────────

// One row of tabs from Emil's dashboard views (owner 2026-10-26); the former
// workspace tabs live inside them. „Cele i realizacja” and „Prognozy” are folded
// into the Pulpit: targets on the tiles, the scenario in the header, „Skąd ta
// liczba” under the tiles. Since 2026-10-27 „Sprzedaż i zamówienia” is the
// sales cut (figures + „Przekrój sprzedaży”), the portfolio and „Wymaga
// uwagi”; the former Analiza (forecast of whole orders) is gone, and a month
// bar of the Pulpit opens that month here (`?tab=orders&month=YYYY-MM`).
// Since PR2 (2026-10-29) one period row above the content serves every tab
// (`reportPeriod.ts`, in the address) and the page loads the read models once.
// „Handlowcy” (2026-10-29, decision handlowcy-marza-oplacona) is ADMIN-only,
// like the ZK lists it folds: other roles do not see the tab.
const TAB_KEYS = ['overview', 'orders', 'salesReps', 'costs', 'cash', 'details'] as const;
type TabKey = typeof TAB_KEYS[number];
const ADMIN_TABS: readonly TabKey[] = ['salesReps'];
const tabAllowed = (tab: TabKey) => !ADMIN_TABS.includes(tab) || authService.canViewForecastVsActual();
// Former tab names still arrive from links and bookmarks.
const TAB_FROM_LEGACY: Record<string, TabKey> = {
  dashboard: 'overview',
  'income-statement': 'details',
  reports: 'details',
  'stock-purchases': 'details',
  analysis: 'orders',
  budget: 'orders',
  'fixed-costs': 'costs',
  'cost-invoices': 'costs',
};

function isTabKey(value: string | null): value is TabKey {
  return !!value && (TAB_KEYS as readonly string[]).includes(value);
}

// `view` is the former dashboard sub-view (?tab=dashboard&view=cash).
function resolveTab(value: string | null, view: string | null = null): TabKey {
  if (isTabKey(value)) return tabAllowed(value) ? value : 'overview';
  if (value === 'dashboard' && isTabKey(view)) return view;
  return (value && TAB_FROM_LEGACY[value]) || 'overview';
}

const Profitability: React.FC = () => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Tab state lives in the URL so links/reloads land on the right view ──
  const requestedTab = searchParams.get('tab');
  const activeTab = resolveTab(requestedTab, searchParams.get('view'));

  useEffect(() => {
    if (requestedTab === 'batches') navigate('/app/inventory/batch-profitability', { replace: true });
  }, [navigate, requestedTab]);

  function handleTabChange(key: string) {
    const tab = resolveTab(key);
    if (tab === activeTab) return;
    const next = new URLSearchParams(searchParams);
    // Links inside the dashboard may name former tabs.
    next.set('tab', tab);
    next.delete('view');
    setSearchParams(next);
  }

  // One period for every tab (PR2, 2026-10-29): in the address, so a tab
  // change, a reload or a copied link keep it; the current year by default.
  const now = useMemo(() => dayjs(), []);
  const period = readReportPeriod(searchParams, now);
  const scenario: DashboardScenario = (DASHBOARD_SCENARIOS as string[]).includes(searchParams.get('scenario') || '')
    ? searchParams.get('scenario') as DashboardScenario
    : 'base';
  const data = useDashboardData(period.year);

  const setPeriod = useCallback((next: ReportPeriod) => {
    setSearchParams(writeReportPeriod(searchParams, next, now), { replace: true });
  }, [searchParams, setSearchParams, now]);

  const setScenario = (next: DashboardScenario) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'base') params.delete('scenario');
    else params.set('scenario', next);
    setSearchParams(params, { replace: true });
  };

  // A month bar of the Pulpit: that month, in „Sprzedaż i zamówienia”.
  const handleOpenMonth = useCallback((month: string) => {
    const next = writeReportPeriod(searchParams, { year: Number(month.slice(0, 4)), kind: 'month', quarter: null, month: Number(month.slice(5, 7)) }, now);
    next.set('tab', 'orders');
    next.delete('view');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams, setSearchParams, now]);

  const periodLabel = period.kind === 'month' && period.month
    ? `${monthLongName(period.month, i18n.language || 'pl', false)} ${period.year}`
    : period.kind === 'quarter' && period.quarter
      ? t('reports:period.quarterOf', { quarter: quarterRoman(period.quarter), year: period.year })
      : String(period.year);
  const periodMonths = useMemo(() => reportPeriodMonths(period), [period.year, period.kind, period.quarter, period.month]); // eslint-disable-line react-hooks/exhaustive-deps
  const yearOptions = Array.from(new Set([now.year() - 1, now.year(), now.year() + 1, period.year]))
    .sort((a, b) => a - b)
    .map((value) => ({ value, label: String(value) }));
  const rangeOptions = [
    { value: 'year', label: t('reports:period.wholeYear') },
    {
      label: t('reports:period.quarters'),
      options: [1, 2, 3, 4].map((quarter) => ({ value: `q${quarter}`, label: t('reports:period.quarter', { quarter: quarterRoman(quarter) }) })),
    },
    {
      label: t('reports:period.months'),
      options: Array.from({ length: 12 }, (_, index) => ({
        value: `m${String(index + 1).padStart(2, '0')}`,
        label: monthLongName(index + 1, i18n.language || 'pl'),
      })),
    },
  ];
  const withScenario = activeTab === 'overview' || activeTab === 'details';
  // Płynność is the state of today: the period does not apply there.
  const periodApplies = activeTab !== 'cash';

  // The page's order list is the one the dashboard data loaded (one request
  // for both, 2026-10-30); this view reads products and the Opiekun from it.
  const orders = useMemo<OrderRow[]>(() => (data.orderRows || []).map((row) => {
    const o = row as OrderRow;
    return {
      ...o,
      revenue: num(o.revenue),
      totalCost: nullableNum(o.totalCost),
      margin: nullableNum(o.margin),
      marginPercent: nullableNum(o.marginPercent),
      products: (o.products || []).map((product) => ({
        ...product,
        quantity: num(product.quantity),
        saleQuantity: num(product.saleQuantity ?? product.quantity),
        unitPrice: num(product.unitPrice),
        totalPrice: num(product.totalPrice),
      })),
    };
  }), [data.orderRows]);

  useEffect(() => {
    if (data.errors.orders) feedback.error(t('reports:profitability.loadOrdersFailed'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.errors.orders]);

  // Loss, missing cost or low margin first; four at most.
  const attentionOrders = useMemo(() => orders
    .filter((o) => (o.totalCost ?? 0) <= 0 || (o.margin ?? 0) < 0 || (o.marginPercent ?? 0) < MARGIN_BUCKET_THRESHOLDS.low)
    .sort((a, b) => {
      const aMargin = a.margin ?? 0;
      const bMargin = b.margin ?? 0;
      const aCost = a.totalCost ?? 0;
      const bCost = b.totalCost ?? 0;
      if (aMargin < 0 && bMargin >= 0) return -1;
      if (bMargin < 0 && aMargin >= 0) return 1;
      if (aCost <= 0 && bCost > 0) return -1;
      if (bCost <= 0 && aCost > 0) return 1;
      return (a.marginPercent ?? 0) - (b.marginPercent ?? 0);
    })
    .slice(0, 4), [orders]);

  // Next to the order statuses in „Sprzedaż i zamówienia”: one list, the
  // order by its full name, a loss in złoty, a low margin in percent, a
  // missing cost without a value (owner 2026-10-29: no „—”).
  const attentionCard = (
    <ReportSection
      className="profitability-attention"
      icon={<WarningOutlined />}
      tone="orange"
      title={t('reports:profitability.attention.title')}
      count={attentionOrders.length}
      lead={attentionOrders.length > 0 ? t('reports:profitability.attention.lead') : undefined}
    >
      {attentionOrders.length === 0 ? (
        <span className="profitability-attention__clear">{t('reports:profitability.attention.clear')}</span>
      ) : (
        <div className="profitability-attention__list">
          {attentionOrders.map((o) => {
            const missing = (o.totalCost ?? 0) <= 0 || o.margin == null;
            const loss = !missing && (o.margin as number) < 0;
            const percent = displayMarginPercent(o);
            const name = `${t('reports:managementDashboard.orderLabel', { number: displayOrderNumber(o) })} · ${o.customer?.name || '—'}`;
            const value = missing ? null : loss ? formatCurrency(o.margin as number, 'PLN') : (percent == null ? null : formatLocalizedPercent(percent, i18n.language));
            return (
              <Link className={`profitability-attention-item ${loss ? 'is-loss' : ''}`} to={`/app/orders/${o.id}`} key={o.id}>
                {loss ? <ExclamationCircleOutlined /> : <WarningOutlined />}
                <span>
                  <strong>{loss ? t('reports:profitability.attention.loss') : missing ? t('reports:profitability.attention.missingCosts') : t('reports:profitability.attention.lowMargin')}</strong>
                  <small title={name}>{name}</small>
                </span>
                {value && <b>{value}</b>}
              </Link>
            );
          })}
        </div>
      )}
    </ReportSection>
  );

  // Account manager and product lines per order for the sales pivot; products
  // keep the Analiza identity so both group a product the same way.
  const pivotOrderInfo = useMemo(() => new Map<string, PivotOrderInfo>(orders.map((order) => [order.id, {
    accountManagerName: order.accountManager?.name || null,
    products: (order.products || []).map((product) => ({
      ...productAnalysisIdentity(product),
      value: Math.max(0, Number(product.totalPrice) || Number(product.saleQuantity) * Number(product.unitPrice) || 0),
    })),
  }])), [orders]);

  // Emil's views in his order; a former workspace tab sits under the view it belongs to.
  const tabLeads: Partial<Record<TabKey, React.ReactNode>> = {
    orders: (
      <OrdersInResultSection
        orderInfo={pivotOrderInfo}
        statement={data.statement}
        failed={data.errors.statement}
        months={periodMonths}
        wholeYear={period.kind === 'year'}
        periodLabel={periodLabel}
      />
    ),
    salesReps: (
      <SalesRepsSection
        statement={data.statement}
        failed={data.errors.statement}
        months={periodMonths}
        periodLabel={periodLabel}
      />
    ),
    cash: <LiquiditySection report={data.liquidity} failed={data.errors.liquidity} onSaved={data.reloadLiquidity} />,
  };
  const tabExtras: Partial<Record<TabKey, React.ReactNode>> = {
    costs: <><FixedCosts periodMonths={periodMonths} periodLabel={periodLabel} /><CostInvoices /></>,
    // The whole year is the statement the page already loaded (no second request).
    details: <IncomeStatementWorkspace
      range={reportPeriodRange(period)}
      shared={period.kind === 'year' ? { data: data.statement, failed: data.errors.statement } : undefined}
    />,
  };
  const tabItems = TAB_KEYS.filter(tabAllowed).map((key) => ({ key, label: t(`reports:managementDashboard.views.${key}.label`) }));
  // A tab that is only its lead (no dashboard view under it).
  const withDashboard = activeTab !== 'salesReps' && activeTab !== 'cash';

  return (
    <div className="profitability-shell">
      <Tabs items={tabItems} activeKey={activeTab} onChange={handleTabChange} />
      {/* One period for every tab (PR2): the same months in every figure, list and statement. */}
      <div className="report-controls profitability-period" data-testid="profitability-period">
        <Select
          value={period.year}
          onChange={(year: number) => setPeriod({ ...period, year })}
          options={yearOptions}
          disabled={!periodApplies}
          aria-label={t('reports:period.year')}
          className="profitability-period__year"
        />
        <Select
          value={reportRangeValue(period)}
          onChange={(value: string) => setPeriod(periodFromRangeValue(period.year, value))}
          options={rangeOptions}
          disabled={!periodApplies}
          aria-label={t('reports:period.range')}
          popupMatchSelectWidth={false}
          className="profitability-period__range"
        />
        {withScenario && (
          <Select<DashboardScenario>
            value={scenario}
            onChange={setScenario}
            options={DASHBOARD_SCENARIOS.map((value) => ({ value, label: t(`reports:managementDashboard.scenario.${value}`) }))}
            aria-label={t('reports:managementDashboard.controls.scenario')}
            className="profitability-period__scenario"
          />
        )}
        {!periodApplies && <span className="report-controls__counter" data-testid="profitability-period-na">{t('reports:period.notApplicable')}</span>}
        <div className="report-controls__end">
          <Tooltip title={t('reports:managementDashboard.footer')} placement="bottomRight">
            <span className="report-controls__counter profitability-period__basis" tabIndex={0}>
              <InfoCircleOutlined /> {t('reports:period.basis')}
            </span>
          </Tooltip>
          <Button icon={<PrinterOutlined />} onClick={() => window.print()}>{t('reports:managementDashboard.controls.print')}</Button>
        </div>
      </div>
      <div className="profitability-flat-stack">
        {tabLeads[activeTab] ?? null}
        {withDashboard && <ManagementDashboard
          view={activeTab as DashboardView}
          data={data}
          period={period}
          periodLabel={periodLabel}
          scenario={scenario}
          onOpenTab={handleTabChange}
          onOpenMonth={authService.canViewForecastVsActual() ? handleOpenMonth : undefined}
          drillDown={activeTab === 'overview'}
          aside={activeTab === 'orders' ? attentionCard : undefined}
        />}
        {tabExtras[activeTab] ?? null}
      </div>
    </div>
  );
};

export default Profitability;
